import { useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { useSocket } from "../../../../../../../connection/socket";
import { addTextAnswer } from "../../../../../../../store/actions/admin/surveys";
import { LanguagesAbbr, Survey, SurveyType, TextAnswer } from "../../../../../../../types/working-model";
import StaggerChildren from "../../../../../../general-ui/animated/stagger-children";
import { ICONS } from "../../../../../../general-ui/icon";
import PanelHeaderOptions from "../../panel/components/panel-header-options";
import { usePageModule } from "../../panel/hooks/panel.hooks";
import { EngageHeader } from "../engage-header";
import QuestionCard from "./components/question-card";
import { useHistory } from "react-router";
import checkIfSurveyIsPoll from "@admin/create/session/editor/surveys/helpers/check-if-survey-is-poll";
import useSurveyResponseCount from "@/components/live-event/session/engage-section/hooks/use-survey-response-count";

import './survey-results.scss';
import { useTypedSelector } from "@/store/reducers/use-typed-selector";
import { selectEditorNavV3 } from "@/store/selectors/feature-flags";
import classNames from "classnames";

const SurveyResults = () => {
	const pageModule = usePageModule();
	const { session: sessionUuid, language } = useParams<{ session: string; language: LanguagesAbbr }>();
	const socket = useSocket(`session-${sessionUuid}-engagement-text`);
	const dispatch = useDispatch();
	const routerHistory = useHistory();
	const isV3 = useTypedSelector(selectEditorNavV3);

	const survey: Survey | undefined = pageModule?.modules?.[0];
	const questions = survey?.questions;

	const { totalResponses } = useSurveyResponseCount({ questions });

	const resLabel = totalResponses === 1 ? 'response' : 'responses';

	useEffect(() => {
		const handleText = (data: TextAnswer) => {
			if (!data?.questionId) return;
			dispatch(addTextAnswer(data.questionId, data));
		};

		socket.addListener('session-engagement-text', handleText);

		return () => {
			socket.removeListener('session-engagement-text', handleText);
		};
	}, [dispatch, socket]);

	const engageMeta = useMemo(() => {
		let type = 'survey';
		let icon = ICONS.SURVEY;
		let label = 'Survey';

		if (survey?.survey_type === SurveyType.quiz) {
			type = 'quiz';
			icon = ICONS.QUIZ;
			label = 'Quiz';
		} else if (checkIfSurveyIsPoll(survey)) {
			type = 'poll';
			icon = ICONS.POLL_FILLED;
			label = 'Poll';
		}

		return { type, icon, label };
	}, [survey]);

	return (
		<div className="survey-results-outer">
			<PanelHeaderOptions>
				<EngageHeader
					language={language}
					pageModule={pageModule}
					onBackClick={() => routerHistory.goBack()}
					engageMeta={engageMeta}
				/>
			</PanelHeaderOptions>
			<div className="session-panel survey-results">
				{!!survey && (
					<>
						{totalResponses === 0 ? (
							<div className={classNames("engage-empty-state", { 'editor-v3': isV3 })}>
								<p>No responses yet</p>
								<p className="engage-empty-state-sub">As soon as someone responds, you'll see it here.</p>
							</div>
						) : (
							<>
								<span>{totalResponses} {resLabel}</span>
								<StaggerChildren className={classNames("survey-results-questions-list", { v3: isV3 })}>
									{survey?.questions.map((question, index) => {
										return (
											<QuestionCard
												key={question.survey_question}
												question={question}
												questionNumber={index + 1}
												type={survey.survey_type ?? SurveyType.survey}
											/>
										);
									})}
								</StaggerChildren>
							</>
						)}
					</>

				)}
			</div>
		</div>
	);
};

export default SurveyResults;
